import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/Header';

import UserManagementTab from './components/UserManagementTab';
import SystemConfigurationTab from './components/SystemConfigurationTab';
import AuditComplianceTab from './components/AuditComplianceTab';

const SystemAdministrationPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const navigationRoutes = [
    { path: '/user-authentication-and-login', label: 'Anmeldung', icon: 'LogIn' },
    { path: '/case-worker-dashboard', label: 'Sachbearbeiter-Dashboard', icon: 'LayoutDashboard' },
    { path: '/client-profile-management', label: 'Kundenprofilverwaltung', icon: 'UserCog' },
    { path: '/client-search-and-directory', label: 'Kundensuche', icon: 'Search' },
    { path: '/unique-id-generation-system', label: 'ID-Generierung', icon: 'Hash' },
    { path: '/bulk-operations-center', label: 'Massenvorgänge', icon: 'Database' },
    { path: '/supervisor-dashboard-and-analytics', label: 'Supervisor-Dashboard', icon: 'BarChart3' },
    { path: '/system-administration-panel', label: 'Systemverwaltung', icon: 'Settings', active: true },
    { path: '/audit-trail-and-compliance-viewer', label: 'Audit & Compliance', icon: 'Shield' }
  ];

  const tabs = [
    { id: 'users', label: 'Benutzerverwaltung', icon: 'Users' },
    { id: 'config', label: 'Systemkonfiguration', icon: 'Settings' },
    { id: 'audit', label: 'Audit & Compliance', icon: 'Shield' }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <Icon name="Building2" size={24} className="text-white" />
            </div>
            {!sidebarCollapsed && (
              <div className="sidebar-brand">
                <div className="text-base font-bold text-slate-900 dark:text-slate-100">
                  Bundesagentur
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  für Arbeit
                </div>
              </div>
            )}
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-section">
              {!sidebarCollapsed && (
                <div className="sidebar-section-title">Navigation</div>
              )}
              {navigationRoutes?.map((route) => (
                <button
                  key={route?.path}
                  onClick={() => navigate(route?.path)}
                  className={`sidebar-item ${route?.active ? 'active' : ''}`}
                >
                  <Icon name={route?.icon} size={20} className="sidebar-item-icon" />
                  <span className="sidebar-item-text">{route?.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </aside>
        {/* Header */}
        <header className={`header ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="header-content">
            <div className="header-left">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  Systemverwaltungspanel
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Systemkonfiguration und Benutzerverwaltung
                </p>
              </div>
            </div>
            <div className="header-right">
              <div className="notification-button">
                <Icon name="Bell" size={20} />
                <span className="notification-badge" />
              </div>
              <div className="header-user">
                <div className="header-user-avatar">AS</div>
                <div className="header-user-info">
                  <div className="header-user-name">Dr. Anna Schmidt</div>
                  <div className="header-user-role">Administrator</div>
                </div>
              </div>
            </div>
          </div>
        </header>
        {/* Main Content */}
        <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="max-w-[1600px] mx-auto p-6 space-y-6">
            {/* System Status Banner */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Systemstatus: Optimal</h2>
                  <p className="text-blue-100">
                    Alle Systeme funktionieren normal. Letzte Wartung: 25.11.2025 02:00 Uhr
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold">99.9%</div>
                    <div className="text-sm text-blue-100">Verfügbarkeit</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">1.2s</div>
                    <div className="text-sm text-blue-100">Antwortzeit</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold">847</div>
                    <div className="text-sm text-blue-100">Aktive Benutzer</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-shadow text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Icon name="UserPlus" size={20} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    Benutzer hinzufügen
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Neuen Systembenutzer erstellen
                </p>
              </button>

              <button className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-shadow text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Icon name="Database" size={20} className="text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    Sicherung starten
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Manuelle Systemsicherung
                </p>
              </button>

              <button className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-shadow text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    Bericht generieren
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Compliance-Bericht erstellen
                </p>
              </button>

              <button className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg transition-shadow text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <Icon name="Activity" size={20} className="text-orange-600 dark:text-orange-400" />
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    System überwachen
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Echtzeit-Systemmetriken
                </p>
              </button>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <div className="border-b border-slate-200 dark:border-slate-700">
                <div className="flex gap-1 p-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                        activeTab === tab?.id
                          ? 'bg-blue-600 text-white' :'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      {tab?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'users' && <UserManagementTab />}
                {activeTab === 'config' && <SystemConfigurationTab />}
                {activeTab === 'audit' && <AuditComplianceTab />}
              </div>
            </div>

            {/* System Health Monitoring */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <Icon name="Server" size={20} />
                  Serverleistung
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">CPU-Auslastung</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">23%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: '23%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">Arbeitsspeicher</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">58%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: '58%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">Festplatte</span>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">67%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500" style={{ width: '67%' }} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <Icon name="AlertTriangle" size={20} />
                  Systemwarnungen
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <Icon name="AlertCircle" size={18} className="text-yellow-600 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        Festplattenspeicher niedrig
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        Nur noch 150 GB verfügbar
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <Icon name="Info" size={18} className="text-blue-600 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        Update verfügbar
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        Version 2.5.1 ist verfügbar
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <Icon name="Clock" size={20} />
                  Geplante Aufgaben
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="Database" size={18} className="text-blue-600" />
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          Tägliche Sicherung
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          Nächste: Heute 02:00
                        </div>
                      </div>
                    </div>
                    <Icon name="CheckCircle" size={18} className="text-emerald-600" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name="RefreshCw" size={18} className="text-purple-600" />
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          Systemwartung
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                          Nächste: 01.12.2025
                        </div>
                      </div>
                    </div>
                    <Icon name="Clock" size={18} className="text-slate-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default SystemAdministrationPanel;